import React from 'react';
import { cn } from '../../../utils/cn';

/**
 * GlassPageHeader - Page hero header component
 *
 * A polished, full-featured page header with glass background, gradient icon,
 * status badge, metadata row, and action buttons. Perfect for entity detail pages.
 *
 * @example
 * ```tsx
 * <GlassPageHeader
 *   title="Unified Advisor Dashboard"
 *   subtitle="Priority #2 in Customer Experience"
 *   description="Deliver a unified dashboard experience..."
 *   icon={<LightningBoltIcon />}
 *   iconGradient="blue-violet"
 *   status={{ label: 'Committed', variant: 'committed' }}
 *   metadata={[
 *     { icon: <BuildingIcon />, label: 'IS', value: 'Customer Experience' },
 *     { icon: <UsersIcon />, label: 'Primary', value: 'Advisor Desktop' },
 *   ]}
 *   actions={
 *     <>
 *       <button>Edit</button>
 *       <button>Share</button>
 *     </>
 *   }
 * />
 * ```
 */

/** Metadata item definition */
export interface HeaderMetadata {
  icon: React.ReactNode;
  label: string;
  value: string;
}

/** Status badge configuration */
export interface HeaderStatus {
  label: string;
  variant: 'committed' | 'stretch' | 'draft' | 'prioritized';
}

export interface GlassPageHeaderProps {
  /** Page/entity title */
  title: string;
  /** Subtitle text (priority, category, etc.) */
  subtitle?: string;
  /** Detailed description */
  description?: string;
  /** Icon element to display */
  icon?: React.ReactNode;
  /** Icon container gradient variant */
  iconGradient?: 'blue-violet' | 'emerald' | 'amber' | 'rose' | 'cyan';
  /** Status badge configuration */
  status?: HeaderStatus;
  /** Array of metadata items to display */
  metadata?: HeaderMetadata[];
  /** Action buttons/elements */
  actions?: React.ReactNode;
  /** Additional CSS classes */
  className?: string;
}

const iconGradientMap = {
  'blue-violet': 'bg-gradient-to-br from-blue-500 to-violet-600',
  emerald: 'bg-gradient-to-br from-emerald-400 to-emerald-600',
  amber: 'bg-gradient-to-br from-amber-400 to-amber-600',
  rose: 'bg-gradient-to-br from-rose-400 to-rose-600',
  cyan: 'bg-gradient-to-br from-cyan-400 to-cyan-600',
};

const statusVariantMap = {
  committed: 'status-committed',
  stretch: 'status-stretch',
  draft: 'status-draft',
  prioritized: 'status-prioritized',
};

/**
 * GlassPageHeader Component
 *
 * Features:
 * - Glass-strong background with premium feel
 * - Gradient icon container with shadow
 * - Status badge next to title
 * - Quick metadata row with icons
 * - Flexible action button area
 * - Responsive flex layout
 * - Section reveal animation support
 */
export const GlassPageHeader: React.FC<GlassPageHeaderProps> = ({
  title,
  subtitle,
  description,
  icon,
  iconGradient = 'blue-violet',
  status,
  metadata,
  actions,
  className,
}) => {
  return (
    <header className={cn('glass-strong rounded-2xl p-6 lg:p-8', className)}>
      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">
        {/* Left: Entity Info */}
        <div className="flex-1 min-w-0">
          {/* Icon and Title Row */}
          <div className="flex items-center gap-3 mb-3">
            {icon && (
              <div
                className={cn(
                  'w-12 h-12 rounded-xl flex items-center justify-center shadow-lg',
                  iconGradientMap[iconGradient]
                )}
              >
                <div className="w-6 h-6 text-white">{icon}</div>
              </div>
            )}
            <div>
              <div className="flex items-center gap-3 flex-wrap">
                <h1 className="text-2xl lg:text-3xl font-bold text-foreground">
                  {title}
                </h1>
                {status && (
                  <span
                    className={cn(
                      'px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-wide',
                      statusVariantMap[status.variant]
                    )}
                  >
                    {status.label}
                  </span>
                )}
              </div>
              {subtitle && (
                <p className="text-muted-foreground/60 text-sm mt-1">{subtitle}</p>
              )}
            </div>
          </div>

          {/* Description */}
          {description && (
            <p className="text-muted-foreground leading-relaxed max-w-2xl">
              {description}
            </p>
          )}

          {/* Metadata Row */}
          {metadata && metadata.length > 0 && (
            <div className="flex flex-wrap items-center gap-4 mt-4 text-sm">
              {metadata.map((item, index) => (
                <div
                  key={index}
                  className="flex items-center gap-2 text-muted-foreground/60"
                >
                  <div className="w-4 h-4">{item.icon}</div>
                  <span>
                    {item.label}: <strong className="text-muted-foreground">{item.value}</strong>
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Right: Actions */}
        {actions && (
          <div className="flex items-center gap-3 flex-shrink-0">
            {actions}
          </div>
        )}
      </div>
    </header>
  );
};

GlassPageHeader.displayName = 'GlassPageHeader';
